/**
 * Fehler des Brain-Clients bei Antworten mit Status >= 400.
 * `message` bleibt bewusst "Request failed: <status> <statusText>" (bestehende Aufrufer und die
 * CSRF-Retry-Logik verlassen sich darauf); Status und Serverantwort stehen zusätzlich als Felder bereit.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  /** `detail` aus dem JSON-Body (String, Liste bei 422 oder anderes), sonst undefined */
  readonly detail: unknown;
  readonly bodyText: string;

  constructor(status: number, statusText: string, bodyText = '') {
    super(`Request failed: ${status} ${statusText}`);
    this.name = 'ApiError';
    this.status = status;
    this.statusText = statusText;
    this.bodyText = bodyText;
    this.detail = parseDetail(bodyText);
  }
}

function parseDetail(bodyText: string): unknown {
  if (!bodyText) return undefined;
  try {
    const parsed = JSON.parse(bodyText) as { detail?: unknown } | null;
    return parsed && typeof parsed === 'object' ? parsed.detail : undefined;
  } catch {
    return undefined;
  }
}

const FIELD_LABELS: Record<string, string> = {
  first_name: 'Vorname', last_name: 'Nachname', street: 'Straße', zip: 'PLZ', city: 'Ort',
  phone: 'Telefon', mobile: 'Mobil', email: 'E-Mail', comment: 'Hinweis', license_plate: 'Kennzeichen',
};

/** Verständliche deutsche Meldung aus einem FastAPI-422-Array (`[{loc, msg}]`). */
function summarizeValidationList(detail: unknown[]): string | undefined {
  const parts: string[] = [];
  for (const item of detail) {
    if (!item || typeof item !== 'object') continue;
    const { loc, msg } = item as { loc?: unknown; msg?: unknown };
    const field = Array.isArray(loc) ? [...loc].reverse().find((l) => typeof l === 'string' && l in FIELD_LABELS) : undefined;
    const label = typeof field === 'string' ? FIELD_LABELS[field] : undefined;
    if (label) parts.push(`${label}: Eingabe ungültig oder zu lang.`);
    else if (typeof msg === 'string') parts.push('Eingabe ungültig.');
  }
  const unique = [...new Set(parts)];
  return unique.length ? unique.join(' ') : undefined;
}

/** Nutzertaugliche Fehlermeldung aus einem ApiError (nur String-Details; 422-Listen zusammengefasst). */
export function apiErrorDetail(e: unknown): string | undefined {
  if (!(e instanceof ApiError)) return undefined;
  if (typeof e.detail === 'string' && e.detail.trim()) return e.detail;
  if (Array.isArray(e.detail)) return summarizeValidationList(e.detail);
  return undefined;
}

/** True bei HTTP 401 (Zugangsdaten ungültig/abgelaufen). */
export function isUnauthorized(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}
